import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface NavItem {
  to: string;
  label: string;
}

const PRODUCTS: NavItem[] = [
  { to: '/lighthouse', label: 'Agent Lighthouse' },
  { to: '/studio', label: 'Agent Studio' },
  { to: '/console', label: 'Agent Console' },
];

const SOLUTIONS: NavItem[] = [
  { to: '/claims', label: 'Claims' },
  { to: '/underwriting', label: 'Underwriting' },
];

const COMPANY: NavItem[] = [
  { to: '/about', label: 'About' },
  { to: '/careers', label: 'Careers' },
  { to: '/contact', label: 'Contact' },
];

function NavDropdown({
  label,
  items,
  active,
  onNavigate,
}: {
  label: string;
  items: NavItem[];
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <div className="group relative">
      <button
        type="button"
        aria-haspopup="true"
        className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-primary ${active ? 'text-primary' : ''}`}
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <div className="invisible absolute left-0 top-full z-50 min-w-[190px] rounded-md border bg-popover py-1.5 opacity-0 shadow-lg transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        {items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className="block px-4 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const isActiveGroup = (items: NavItem[]) => items.some((item) => item.to === location.pathname);
  const navLinkClass = (active: boolean) =>
    `text-sm font-medium transition-colors hover:text-primary ${active ? 'text-primary' : ''}`;

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center gap-6 px-4">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <img src="/assets/logos/brand-icon-square-animated.svg" alt="" className="h-10 w-10" />
          <img
            src="/assets/logos/CriticalBridgeLogoName.png"
            alt="CriticalBridge.AI"
            className="h-6"
          />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-6 lg:flex">
          <Link to="/platform" className={navLinkClass(isActive('/platform'))}>
            Platform
          </Link>
          <NavDropdown label="Products" items={PRODUCTS} active={isActiveGroup(PRODUCTS)} />
          <NavDropdown label="Solutions" items={SOLUTIONS} active={isActiveGroup(SOLUTIONS)} />
          <Link to="/trust" className={navLinkClass(isActive('/trust'))}>
            Trust
          </Link>
          <NavDropdown label="Company" items={COMPANY} active={isActiveGroup(COMPANY)} />
        </nav>

        <div className="hidden shrink-0 items-center gap-5 lg:flex">
          <a
            href="https://partners.criticalbridge.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Partner Login
          </a>
          <Button asChild>
            <Link to="/trial">Start the Discovery Trial</Link>
          </Button>
        </div>

        <button
          type="button"
          className="ml-auto lg:hidden"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-label="Toggle navigation menu"
          aria-expanded={isMenuOpen}
        >
          {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {isMenuOpen && (
        <div className="border-t bg-background p-4 lg:hidden">
          <nav className="flex flex-col gap-1">
            <Link
              to="/platform"
              className="rounded-md px-2 py-2 text-sm font-medium hover:bg-accent"
              onClick={() => setIsMenuOpen(false)}
            >
              Platform
            </Link>

            <div className="mt-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Products
            </div>
            {PRODUCTS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-md px-4 py-2 text-sm hover:bg-accent"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            <div className="mt-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Solutions
            </div>
            {SOLUTIONS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-md px-4 py-2 text-sm hover:bg-accent"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            <Link
              to="/trust"
              className="mt-2 rounded-md px-2 py-2 text-sm font-medium hover:bg-accent"
              onClick={() => setIsMenuOpen(false)}
            >
              Trust
            </Link>

            <div className="mt-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Company
            </div>
            {COMPANY.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-md px-4 py-2 text-sm hover:bg-accent"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            <a
              href="https://partners.criticalbridge.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 rounded-md px-2 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
            >
              Partner Login
            </a>

            <Button asChild className="mt-3">
              <Link to="/trial" onClick={() => setIsMenuOpen(false)}>
                Start the Discovery Trial
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
